import React, { useEffect, useState } from 'react';
import { OfficeEmpPreview } from '../PaySlipPreview/components/OfficeEmp/OfficeEmpPreview';
import { ConstructionEmpPreview } from '../PaySlipPreview/components/ConstructionEmp/ConstructionEmpPreview';
import { fileUploadService } from '../../services/FileUploadService';
import { EmployeeDocuments } from '../DocumentDelivery/EmployeeDocuments';
import './MyPayslips.css';

/** Prefer login 現場氏名 (dual), then 本社氏名 — used when 本社コード ≠ construction_workers.emp_code */
function getConstructionNameFallback(officeNameFromApi: string): string {
  try {
    const raw = sessionStorage.getItem('currentEmployee');
    if (raw) {
      const e = JSON.parse(raw) as { constructionWorkerName?: string; name?: string };
      const cw = (e.constructionWorkerName ?? '').trim();
      if (cw) return cw;
      const n = (e.name ?? '').trim();
      if (n) return n;
    }
  } catch {
    /* ignore */
  }
  return (officeNameFromApi ?? '').trim();
}

export const MyPayslips: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [employeeName, setEmployeeName] = useState<string>('');
  const [payslipKind, setPayslipKind] = useState<'office' | 'construction' | 'documents'>('office');

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setApiError(undefined);

    fileUploadService
      .getEmployeeByCode(employeeId)
      .then((emp) => {
        if (cancelled) return;
        setEmployeeName(emp?.name ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setApiError(`従業員情報の取得に失敗しました: ${msg}`);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="error-container">
        <h2>エラー</h2>
        <p>{apiError}</p>
      </div>
    );
  }

  return (
    <div className="my-payslips-page">
      <div className="tab-container employee-payslip-tab-root">
        <div className="tab-header">
          <h3>自分の帳票</h3>
          <p>本社・現場の給与明細と、交付された書類を確認できます</p>
        </div>

        <nav className="tab-navigation" role="tablist" aria-label="自分の帳票">
          <button
            type="button"
            role="tab"
            aria-selected={payslipKind === 'office'}
            className={`tab-button ${payslipKind === 'office' ? 'active' : ''}`}
            onClick={() => setPayslipKind('office')}
          >
            本社
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={payslipKind === 'construction'}
            className={`tab-button ${payslipKind === 'construction' ? 'active' : ''}`}
            onClick={() => setPayslipKind('construction')}
          >
            現場
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={payslipKind === 'documents'}
            className={`tab-button ${payslipKind === 'documents' ? 'active' : ''}`}
            onClick={() => setPayslipKind('documents')}
          >
            書類
          </button>
        </nav>

        <div className="tab-content employee-payslip-tab-content">
          <div className="tab-panel">
            {payslipKind === 'office' && (
              <OfficeEmpPreview
                isEmployeeView={true}
                currentEmployeeCode={employeeId}
                currentEmployeeName={employeeName}
              />
            )}
            {payslipKind === 'construction' && (
              <ConstructionEmpPreview
                isEmployeeView={true}
                currentEmployeeCode={employeeId}
                fallbackConstructionWorkerName={getConstructionNameFallback(employeeName)}
              />
            )}
            {payslipKind === 'documents' && (
              <div className="my-documents-panel">
                <EmployeeDocuments embedded />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

